<template>
  <Transition appear>
    <div
      v-if="showTooltip"
      ref="tooltip"
      :style="tooltipStyle"
      :class="tooltipClass"
      class="rounded-small bg-inverse-surface text-body-small text-on-inverse-surface text-inverse-on-surface pointer-events-none fixed top-0 left-0 z-[999] flex h-6 items-center px-2 whitespace-nowrap delay-200 select-none"
    >
      <span>{{ tooltipContent }}</span>
    </div>
  </Transition>
</template>
<script lang="ts" setup>
  import { computed, useTemplateRef, watch } from 'vue'
  import { initTooltip } from './tooltip-directive'
  import { useElementBounding } from '@vueuse/core'

  const { showTooltip, tooltipPosition, tooltipContent } = initTooltip()

  const tooltip = useTemplateRef('tooltip')
  const { width, height } = useElementBounding(tooltip)

  const TOOLTIP_GAP = 4

  type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

  const position = computed<TooltipPosition>(() => {
    const windowHeight = document.documentElement.clientHeight
    const windowWidth = document.documentElement.clientWidth
    const { y, parentHeight, x, parentWidth } = tooltipPosition.value

    const spaceTop = y
    const spaceBottom = windowHeight - (y + parentHeight)
    const spaceLeft = x - parentWidth / 2
    const spaceRight = windowWidth - (x + parentWidth / 2)

    const fitsTop = spaceTop >= height.value + TOOLTIP_GAP
    const fitsBottom = spaceBottom >= height.value + TOOLTIP_GAP
    const fitsLeft = spaceLeft >= width.value + TOOLTIP_GAP
    const fitsRight = spaceRight >= width.value + TOOLTIP_GAP

    if (fitsTop) return 'top'
    if (fitsBottom) return 'bottom'
    if (fitsLeft) return 'left'
    if (fitsRight) return 'right'

    // Default to top if nothing fits
    return 'top'
  })

  const y = computed(() => {
    const { y: parentY, parentHeight } = tooltipPosition.value

    switch (position.value) {
      case 'top':
        return parentY - height.value - TOOLTIP_GAP
      case 'bottom':
        return parentY + parentHeight + TOOLTIP_GAP
      case 'left':
      case 'right':
        return parentY + parentHeight / 2 - height.value / 2
    }
  })

  const x = computed(() => {
    const windowWidth = document.documentElement.clientWidth
    const { x: parentX, parentWidth } = tooltipPosition.value

    switch (position.value) {
      case 'top':
      case 'bottom': {
        // Horizontal centering with edge clamping
        if (parentX - width.value / 2 < 0) {
          return width.value / 2
        }
        if (parentX + width.value / 2 > windowWidth) {
          return windowWidth - width.value / 2
        }
        return parentX
      }
      case 'left':
        return parentX - parentWidth / 2 - width.value - TOOLTIP_GAP
      case 'right':
        return parentX + parentWidth / 2 + TOOLTIP_GAP
    }
  })

  const tooltipStyle = computed(() => ({
    top: y.value + 'px',
    left: x.value + 'px',
  }))

  const tooltipClass = computed(() => {
    // Apply transform based on position for proper anchor alignment
    if (position.value === 'top' || position.value === 'bottom') {
      return `-translate-x-1/2`
    }
    return ''
  })

  watch(
    () => tooltip.value,
    function (newElement) {
      if (newElement) {
        width.value = newElement.offsetWidth
      }
    },
  )
</script>
<style>
  .v-enter-active {
    transition: opacity 0.3s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
</style>
